import * as React from 'react';

import { useQuery } from '@apollo/client';
import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import CalendarIcon from '@crehana/icons/outline/Calendar';
import { CustomNextImage as Image } from '@crehana/next';
import {
  Col,
  Container,
  InverseButton,
  PrimaryButton,
  Row,
  Slider,
  // biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
} from '@crehana/ui';
import { date, getImgxUrl } from '@crehana/utils';

import GET_MICRODEGREE_DATA from '@/views/Campaign/graphql/CampaingLandingMicrodegree.graphql';
import type { CampaingLandingMicrodegree } from '@/views/Campaign/graphql/types/CampaingLandingMicrodegree';
import type { CampaignProps } from '@/views/Campaign/types';

import { imagesData } from './images-data';
import Loader from './Loader';
import {
  LeftContent,
  MicrodegreeCardWrapper,
  SliderWrapper,
  Wrapper,
} from './Microdegree.styles';
import type { MicrodegreeCard } from './types';

const Microdegree: React.FC<
  React.PropsWithChildren<CampaignProps['microdegrees']>
> = ({ backgroundDesktop, backgroundMobile, title, description }) => {
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const [microdegreeData, setMicrodegreeData] = React.useState<
    MicrodegreeCard[]
  >([]);
  const { loading } = useQuery<CampaingLandingMicrodegree>(
    GET_MICRODEGREE_DATA,
    {
      onCompleted(data) {
        const microdegrees = data.degreesAssets?.edges.map(
          item => item?.node as MicrodegreeCard,
        );

        if (microdegrees && microdegrees.length) {
          setMicrodegreeData(microdegrees);
        }
      },
    },
  );

  const handleCardPrimaryButton = ({ degree }: { degree: MicrodegreeCard }) => {
    logEvent({
      event: 'Click Microdegree Card Primary Button',
      data: {
        ...getSourceEventParams(),
        Degree: degree,
      },
    });
    return true;
  };

  const handleCardSecondaryButton = ({
    degree,
  }: {
    degree: MicrodegreeCard;
  }) => {
    logEvent({
      event: 'Click Microdegree Card Secondary Button',
      data: {
        ...getSourceEventParams(),
        Degree: degree,
      },
    });
    return true;
  };

  if (loading || !microdegreeData.length) {
    return <Loader />;
  }

  return (
    <Wrapper
      id="microdegrees-section"
      className="lg:flex items-center lg:py-0"
      bgDesktop={getImgxUrl(backgroundDesktop || '', {
        auto: 'format',
      })}
      bgMobile={getImgxUrl(backgroundMobile || '', {
        auto: 'format',
      })}
    >
      <Container>
        <Row>
          <Col lg={4}>
            <LeftContent className="flex flex-col justify-center h-full text-center m-auto lg:text-left">
              <h2 className="font-h1 mt-32 lg:mt-0 text-white">{title}</h2>
              <p className="font-body2 my-32 text-white">{description}</p>
            </LeftContent>
          </Col>
          <Col lg={8}>
            <SliderWrapper className="m-auto lg:ml-auto lg:mr-0">
              <Slider
                desktopPaginationSelector=".microdegree-pagination"
                desktopPaginationType="default-s"
                desktopPaginationProps={{
                  isDark: true,
                }}
                mobilePaginationProps={{
                  type: 'dots',
                }}
                itemsPerPage={{
                  lg: 2,
                  md: 1,
                  sm: 1,
                  xs: 1,
                }}
              >
                {microdegreeData.map(degree => {
                  return (
                    <MicrodegreeCardWrapper
                      key={degree.slug}
                      className="relative rounded-10 overflow-hidden"
                    >
                      <Image
                        src={
                          imagesData[
                            degree.slug as unknown as keyof typeof imagesData
                          ]
                        }
                        alt={degree.title || ''}
                        quality={90}
                        objectFit="cover"
                        objectPosition="top center"
                        width={380}
                        height={550}
                        loading="lazy"
                      />
                      <div className="flex mb-40 px-24 sm:mb-52 sm:px-48 flex-col justify-center w-full absolute bottom-0 z-3">
                        <p className="font-subtitle2 text-white text-left mb-16">
                          {degree.title}
                        </p>
                        <div className="flex items-center mb-16">
                          <CalendarIcon
                            size={18}
                            color="white"
                            className="mr-12"
                          />
                          <p className="font-body3 text-white text-left ">
                            {`${t('DEGREE_STARTS_ON')} ${date.format(
                              degree.syllabus?.startDate,
                              'DD/MM',
                            )}`}
                          </p>
                        </div>
                        <PrimaryButton
                          label={t('MICRODEGREE_CARD_PRIMARY_BUTTON')}
                          isDark
                          as="a"
                          size="s"
                          href={
                            degree.checkoutUrl || `/microdegree/${degree.slug}`
                          }
                          onClick={() => handleCardPrimaryButton({ degree })}
                        />
                        <InverseButton
                          className="mt-8"
                          size="s"
                          label={t('MICRODEGREE_CARD_SECONDARY_BUTTON')}
                          isDark
                          as="a"
                          href={`/microdegree/${degree.slug}`}
                          onClick={() => handleCardSecondaryButton({ degree })}
                        />
                      </div>
                    </MicrodegreeCardWrapper>
                  );
                })}
              </Slider>
              <div className="microdegree-pagination w-full flex justify-center mt-24 text-white" />
            </SliderWrapper>
          </Col>
        </Row>
      </Container>
    </Wrapper>
  );
};

export default Microdegree;
